/**
 * Bank Transactions Center - Constants and Helper Functions
 * ==================================================
 * Extracted from BankFeedCenterPage.jsx for better maintainability.
 * These are pure functions and constants with no side effects.
 * 
 * IMPORTANT: This file is imported by BankFeedCenterPage.jsx
 * Do not modify without testing the Bank Transactions Center thoroughly.
 */

/**
 * Classification options for reclassifying bank feed transactions.
 * Used in the reclassification dropdown/dialog.
 */
export const CLASSIFICATION_OPTIONS = [
  // Inflows
  { value: 'customer_payment', label: 'Customer Payment', direction: 'inflow' },
  { value: 'customer_deposit', label: 'Customer Deposit/Advance', direction: 'inflow' },
  { value: 'loan_received', label: 'Loan Received', direction: 'inflow' },
  { value: 'capital_contribution', label: 'Capital Contribution', direction: 'inflow' },
  { value: 'interest_income', label: 'Interest Income', direction: 'inflow' },
  { value: 'tax_refund', label: 'Tax Refund', direction: 'inflow' },
  { value: 'vendor_refund', label: 'Vendor Refund', direction: 'inflow' },
  { value: 'insurance_proceeds', label: 'Insurance Proceeds', direction: 'inflow' },
  { value: 'grant_received', label: 'Grant Received', direction: 'inflow' },
  // Outflows
  { value: 'bill_payment', label: 'Bill Payment', direction: 'outflow' },
  { value: 'payroll_payment', label: 'Payroll/Salary Payment', direction: 'outflow' },
  { value: 'loan_repayment', label: 'Loan Repayment', direction: 'outflow' },
  { value: 'owner_drawing', label: 'Owner Drawing', direction: 'outflow' },
  { value: 'dividend_payment', label: 'Dividend Payment', direction: 'outflow' },
  { value: 'tax_remittance', label: 'Tax Remittance', direction: 'outflow' },
  { value: 'vat_payment', label: 'VAT Payment', direction: 'outflow' },
  { value: 'bank_charges', label: 'Bank Charges', direction: 'outflow' },
  { value: 'fixed_asset_purchase', label: 'Fixed Asset Purchase', direction: 'outflow' },
  { value: 'customer_refund', label: 'Customer Refund', direction: 'outflow' },
  // Both directions
  { value: 'internal_transfer', label: 'Internal Transfer', direction: 'both' },
];

/**
 * Friendly names mapping for AI classification codes.
 * Maps technical classification codes to human-readable descriptions.
 */
export const FRIENDLY_CLASSIFICATION_NAMES = {
  'bank_charges': 'a Bank Fee/Charge',
  'bank_fees': 'a Bank Fee/Charge',
  'software_subscriptions': 'Software/Subscription Expense',
  'consulting_fees': 'a Consulting/Professional Fee',
  'owner_drawings': "Owner's Personal Withdrawal",
  'service_revenue': 'Income from Services',
  'sales_revenue': 'Sales Income',
  'interest_income': 'Interest Earned',
  'loan_received': 'a Loan Receipt',
  'capital_contribution': 'Capital Investment',
  'payroll_expense': 'Salary/Payroll Payment',
  'rent_expense': 'Rent Payment',
  'utilities_expense': 'Utility Bill Payment',
  'office_expenses': 'Office/Admin Expense',
  'advertising_expense': 'Marketing/Advertising Cost',
  'travel_expense': 'Travel Expense',
  'internal_transfer': 'an Internal Transfer',
  'vat_charges': 'VAT/Tax Charge',
  'stamp_duties': 'Stamp Duty Fee'
};

/**
 * Document type friendly names mapping.
 */
export const DOC_TYPE_NAMES = {
  'invoice': 'Customer Payment (Invoice)',
  'bill': 'Vendor Payment (Bill)',
  'other_receipt': 'Other Income',
  'other_payment': 'Other Expense'
};

/**
 * Convert AI classification to plain English description.
 * Used to display user-friendly classification names in the UI.
 * 
 * @param {Object} item - Bank feed transaction item
 * @returns {string} Human-readable classification description
 */
export const getPlainEnglishClassification = (item) => {
  const subCategory = item.ai_sub_category || item.classification;
  const docType = item.ai_document_type;
  
  // Check friendly names first
  if (subCategory && FRIENDLY_CLASSIFICATION_NAMES[subCategory.toLowerCase()]) {
    return FRIENDLY_CLASSIFICATION_NAMES[subCategory.toLowerCase()];
  }
  
  // Fall back to formatted sub_category
  if (subCategory) {
    return subCategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
  
  // Fall back to document type names
  if (docType) {
    return DOC_TYPE_NAMES[docType] || docType.replace(/_/g, ' ');
  }
  
  return 'a Business Transaction';
};

/**
 * Get readable classification label from classification value.
 * Looks up the value in CLASSIFICATION_OPTIONS and returns the label.
 * 
 * @param {string} classification - Classification value (e.g., 'customer_payment')
 * @returns {string} Human-readable label (e.g., 'Customer Payment')
 */
export const getReadableClassification = (classification) => {
  if (!classification) return 'Unclassified';
  
  const option = CLASSIFICATION_OPTIONS.find(opt => opt.value === classification);
  if (option) return option.label;
  
  // Fall back to formatted string
  return classification.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Filter classification options by transaction direction.
 * 
 * @param {string} direction - 'inflow', 'outflow', or 'all'
 * @returns {Array} Filtered classification options
 */
export const getClassificationOptionsForDirection = (direction) => {
  if (direction === 'all') return CLASSIFICATION_OPTIONS;
  
  return CLASSIFICATION_OPTIONS.filter(
    opt => opt.direction === direction || opt.direction === 'both'
  );
};

/**
 * Default initial state for stats (used for caching).
 */
export const DEFAULT_STATS = {
  total: 0,
  completed: 0,
  review_needed: 0,
  duplicates: 0,
  failed: 0
};

/**
 * Default training example structure.
 */
export const DEFAULT_TRAINING_EXAMPLE = {
  description: '',
  counterparty: '',
  direction: 'outflow',
  classification: '',
  gl_code: '',
  gl_name: '',
  amount_range: 'any',
  notes: ''
};

/**
 * Cache duration in milliseconds.
 * iter1086h Feb 2026: reduced from 120s -> 15s so counts don't drift
 * from the database after a sync / classification / recon action.
 */
export const STATS_CACHE_DURATION = 15000;

/**
 * Local storage keys for Bank Transactions Center.
 */
export const STORAGE_KEYS = {
  STATS_CACHE: 'bankfeed_stats_cache',
  ACTIVE_JOBS: 'bankfeed_active_jobs'
};

export default {
  CLASSIFICATION_OPTIONS,
  FRIENDLY_CLASSIFICATION_NAMES,
  DOC_TYPE_NAMES,
  getPlainEnglishClassification,
  getReadableClassification,
  getClassificationOptionsForDirection,
  DEFAULT_STATS,
  DEFAULT_TRAINING_EXAMPLE,
  STATS_CACHE_DURATION,
  STORAGE_KEYS
};
